import React, { useMemo } from 'react';
import LegacyPratusTable from 'components/DesignSystems/PratusTable/LegacyPratusTable';
import RosterExpandedRow from './RosterExpandedRow';
import { RosterTableProps } from './types';
import {
  useGetIncidentLogs,
  IncidentLogEntry,
} from 'hooks/incident/useWorkLog';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

// Constants moved outside component
const TOGGLE_DATA_CHECK_IN = [
  { label: 'Checked-In', value: 'Checked-In' },
  { label: 'Checked-Out', value: 'Checked-Out' },
];

const TOGGLE_DATA_SIGN_IN = [
  { label: 'Signed-In', value: 'Signed-In' },
  { label: 'Signed-Out', value: 'Signed-Out' },
];

const formatRosterTimestamp = (
  iso: string | null | undefined
): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
};

const StatusWithSubline: React.FC<{
  status: React.ReactNode;
  timestampIso: string | null | undefined;
}> = ({ status, timestampIso }) => {
  const formatted = formatRosterTimestamp(timestampIso);
  return (
    <div>
      <div>{status}</div>
      {formatted ? (
        <small className="text-muted d-block">{formatted}</small>
      ) : null}
    </div>
  );
};

const getLatestActiveSession = (
  userGuid: string,
  incidentLogs?: IncidentLogEntry[]
) => {
  if (!incidentLogs) return null;

  const userLog = incidentLogs.find((log) => log.userGuid === userGuid);
  if (!userLog?.logs || userLog.logs.length === 0) return null;

  const allSessions = userLog.logs.flatMap((log) => log.sessions || []);

  const sortedSessions = allSessions
    .filter((session) => session.signInTime)
    .sort(
      (a, b) =>
        new Date(b.signInTime).getTime() - new Date(a.signInTime).getTime()
    );

  return sortedSessions.length > 0 ? sortedSessions[0] : null;
};

const getLatestLogoutFromWorkLogs = (
  userGuid: string,
  incidentLogs?: IncidentLogEntry[]
): string | null => {
  if (!incidentLogs) return null;
  const userLog = incidentLogs.find((log) => log.userGuid === userGuid);
  if (!userLog?.logs?.length) return null;
  const withLogout = userLog.logs
    .filter((l) => l.logoutTime)
    .sort(
      (a, b) =>
        new Date(b.logoutTime!).getTime() - new Date(a.logoutTime!).getTime()
    );
  return withLogout[0]?.logoutTime ?? null;
};

const RosterTable: React.FC<RosterTableProps> = ({
  rosterData,
  isRosterLoading,
  editMemberButtonClicked,
  removeMemberButtonClicked,
  removeSelfFromIncidentButtonClicked,
  onDeleteSchedule,
  onToggleButtonClick,
  simplified = false,
  currentUserHasApprovalAuthority,
  onToggleApprovalAuthority,
  isTogglingAuthority,
  showApprovalAuthority = true,
}) => {
  const { data: incidentLogs } = useGetIncidentLogs();

  const columns = useMemo(() => {
    return [
      {
        dataField: 'emailAddress',
        text: 'Incident Member',
        sort: true,
        headerAttrs: { title: 'Sort By:' },
        attrs: { title: 'Incident Member' },
      },
      {
        dataField: 'incidentSeats',
        text: 'Incident Seats',
        sort: true,
        attrs: { title: 'Incident Seats' },
        formatter: (_cell: undefined, row: RosterEntry) => {
          return (
            row.incidentPositions.map((pos) => pos.positionName).join(', ') ||
            '-'
          );
        },
      },
      {
        dataField: 'activation_status',
        text: 'Activation Status',
        sort: true,
        attrs: { title: 'Activation Status' },
        formatter: (_cell: undefined, row: RosterEntry) => {
          if (row.activatedTimestamp) {
            return (
              <StatusWithSubline
                status={<span className="text-success">Activated</span>}
                timestampIso={row.activatedTimestamp.toString()}
              />
            );
          }
          if (row.declinedTimestamp) {
            return (
              <StatusWithSubline
                status={<span className="text-danger">Declined</span>}
                timestampIso={row.declinedTimestamp.toString()}
              />
            );
          }
          return (
            <StatusWithSubline
              status={<span className="text-warning">Pending</span>}
              timestampIso={undefined}
            />
          );
        },
      },
      {
        dataField: 'checkin_status',
        text: 'Check-In Status',
        sort: true,
        attrs: { title: 'Check-In Status' },
        formatter: (_cell: undefined, row: RosterEntry) => {
          if (row.active && row.activeTime) {
            return (
              <StatusWithSubline
                status={<span className="text-success">Checked In</span>}
                timestampIso={row.activeTime.toString()}
              />
            );
          }
          return (
            <StatusWithSubline
              status={<span className="text-muted">Checked Out</span>}
              timestampIso={getLatestLogoutFromWorkLogs(
                row.userId,
                incidentLogs
              )}
            />
          );
        },
      },
      {
        dataField: 'activationNote',
        text: 'Note',
        sort: true,
        attrs: { title: 'Activation Note' },
        formatter: (cell: string | null | undefined) => {
          if (cell && cell.length > 30) {
            return <span title={cell}>{cell.substring(0, 30)}...</span>;
          }
          return cell || '-';
        },
      },
      ...(showApprovalAuthority
        ? [
            {
              dataField: 'approval_authority',
              text: 'Approval Authority',
              sort: true,
              attrs: { title: 'Approval Authority' },
              formatter: (_cell: undefined, row: RosterEntry) => {
                return <span>{row.approvalAuthority ? 'Yes' : 'No'}</span>;
              },
            },
          ]
        : []),
      {
        dataField: 'signin_status',
        text: 'Sign-In Status (Daily)',
        sort: true,
        attrs: { title: 'Sign-In Status (Daily)' },
        formatter: (_cell: undefined, row: RosterEntry) => {
          const latestSession = getLatestActiveSession(
            row.userId,
            incidentLogs
          );
          const isSignedIn =
            latestSession?.signInTime && !latestSession.signOutTime;
          const dailyTimestampIso = latestSession
            ? isSignedIn
              ? latestSession.signInTime
              : (latestSession.signOutTime ?? undefined)
            : undefined;
          if (isSignedIn) {
            return (
              <StatusWithSubline
                status={<span className="text-success">Signed In</span>}
                timestampIso={dailyTimestampIso}
              />
            );
          }
          return (
            <StatusWithSubline
              status={<span className="text-muted">Signed Out</span>}
              timestampIso={dailyTimestampIso}
            />
          );
        },
      },
    ];
  }, [incidentLogs, showApprovalAuthority]);
  const expandRow = {
    parentClassName: 'parent-expand-tr',
    className: 'child-expand-tr',
    renderer: (row: RosterEntry) => (
      <RosterExpandedRow
        row={row}
        onDeleteSchedule={onDeleteSchedule}
        onToggleButtonClick={onToggleButtonClick}
        isRosterLoading={isRosterLoading}
        rosterData={rosterData}
        removeMemberButtonClicked={removeMemberButtonClicked}
        removeSelfFromIncidentButtonClicked={
          removeSelfFromIncidentButtonClicked
        }
        editMemberButtonClicked={editMemberButtonClicked}
        toggleDataCheckIn={TOGGLE_DATA_CHECK_IN}
        toggleDataSignIn={TOGGLE_DATA_SIGN_IN}
        simplified={simplified}
        currentUserHasApprovalAuthority={currentUserHasApprovalAuthority}
        onToggleApprovalAuthority={onToggleApprovalAuthority}
        isTogglingAuthority={isTogglingAuthority}
        showApprovalAuthority={showApprovalAuthority}
      />
    ),
    showExpandColumn: true,
  };

  return (
    <LegacyPratusTable
      keyField={'userId'}
      columns={columns}
      rows={rosterData || []}
      expandRow={expandRow}
    />
  );
};

export default RosterTable;
